import * as THREE from 'three';
import { CrossfireLevel } from './CrossfireLevel.js';

/**
 * "Assault" — a reconstruction of CryptR's Counter-Strike hostage-rescue map.
 *
 * Unlike Crossfire there is no published Hammer source, so this is converted
 * from the compiled GoldSrc BSP: exact face geometry, real texture axes and
 * texture names come out of the lump data, and an average RGB per texture comes
 * from the map's own WAD where it ships a custom texture. Nothing of Valve's or
 * CryptR's art is redistributed — only geometry and a colour per name, rendered
 * with our own procedural PBR surfaces.
 *
 * The whole level implementation (materials, brush entities, doors, lifts,
 * ladders, buttons) is shared with CrossfireLevel; only the payload differs.
 */
export class AssaultLevel extends CrossfireLevel {
  static displayName = 'ASSAULT';
  static description = 'Counter-Strike · CryptR · 1999';
  static mapFile = 'assault.json';

  /**
   * cs_assault is a hostage map — its BSP has no `func_bomb_target`. These are
   * authored: two open, walkable areas roughly equidistant from the real CT and
   * T spawns (15.6 m / 14.5 m), 74.5 m apart, chosen with tools/_sitescout so
   * neither side trivially owns both. A = the yard, B = the warehouse side.
   */
  static bombSites = [
    { label: 'A', box: [-22.5, -0.2, 18.5, -13.5, 3.0, 27.5] },
    { label: 'B', box: [25.5, 0.6, -38.5, 34.5, 3.8, -29.5] },
  ];

  /**
   * Assault is a NIGHT map. This is not a stylistic choice — the skybox it
   * shipped with (`gfx/env/city1*.tga`) is a dark overcast city, and sampling
   * those six faces gives zenith #5e5d71, horizon ~#4a4a58 and ground #1b1b1c,
   * with mean luminance 0.11-0.37. Rendering it under the daylight rig the
   * other two maps use was the single largest appearance error.
   *
   * So: no sun to speak of (a dim cool moon just strong enough to give props a
   * direction and a soft contact shadow), the hemisphere doing most of the
   * work in the sampled sky/ground colours, and a flat gradient dome instead of
   * the Preetham sky — a physical daytime model at low elevation renders a
   * sunset, not night. Exposure is raised to compensate for the much lower key.
   */
  static atmosphere = {
    skyGradient: [0x5e5d71, 0x4a4a58],   // sampled: city1up zenith, side tops
    elevation: 34, azimuth: 205,
    sunColor: 0xaebbdd, sunIntensity: 1.5,
    hemiSky: 0x5e5d71, hemiGround: 0x1b1b1c, hemiIntensity: 2.1,
    bounceColor: 0x8f9ec6, bounceIntensity: 0.10,
    envIntensity: 0.85, vmEnvIntensity: 0.55,
    exposure: 1.15,
    // Heavy overcast, which is what the skybox actually shows: a low dense deck
    // with almost no gaps, lit from below by the city rather than from above by
    // a moon. Low altitude so the cells are big and close, and it is baked into
    // the IBL so it genuinely flattens the ambient instead of being a decal.
    clouds: {
      coverage: 0.88, density: 1.0, altitude: 620, scale: 0.0021,
      wind: [1, 0.6], speed: 0.006,
      tint: 0x6a6a80, shade: 0x2e2e3c, detail: 1.05, opacity: 0.95,
    },
  };

  constructor(engine, data) {
    super(engine, data);

    // Panel lighting runs in the base constructor now. Hostage rooms get a practical on top, because those are the rooms the
    // mode sends you into and they are deepest inside the building.
    for (const p of this.data.poi ?? []) {
      const light = new THREE.PointLight(0xffe0b4, 9, 14, 2);
      light.position.set(p.pos[0], p.pos[1] + 1.6, p.pos[2]);
      this.decor.add(light);
    }
  }
}
